import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { Link, LinkTypeInfoMap, UpsertLink } from '@/models/link.model';
import { getLink, upsertLink } from '@/services/link.service';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, SelectPicker, FormInstance } from 'rsuite';
import { toastErrorMessage } from '@/utils/toasterMessage';


const modelSchema = Schema.Model<UpsertLink>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  linkPath: Schema.Types.StringType().isRequired('This field is required.'),
  type: Schema.Types.NumberType().isRequired('This field is required.')
});

interface LinkDrawerProps extends DrawerProps{
  linkId?: number,
  runningClubId?: number,
  encyclopediaItemId?: number
};

const LinkDrawer = (props: LinkDrawerProps) => {
  const { linkId:id, runningClubId, encyclopediaItemId, open, onClose, ...rest } = props;

  const emptyModel: UpsertLink = {
    id: undefined,
    name: undefined,
    linkPath: '',
    runningClubId: runningClubId,
    encyclopediaItemId: encyclopediaItemId,
    type: undefined,
  };

  const [formValue, setFormValue] = useState<UpsertLink>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
  
  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormValue(emptyModel);
    }
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<Link> = 
          await getLink(id);
        if (result.success) {
          setFormValue({ ...result.result});
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching link:${errorModel?.errorMessage}`);        
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<Link> = await upsertLink(formValue);
      
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue, 
      ...value 
    }));
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" 
      open={open}
      onClose={handleDrawerClose} 
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{id ? 'Edit a' : 'Add a new'} Link</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-link' fluid 
          ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}
        >
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control name="name"/>
          </Form.Group>
          <Form.Group>
            <Form.Label>Link</Form.Label>
            <Form.Control name="linkPath" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Type</Form.Label>
              <Form.Control name="type" block
                searchable={false}
                accepter={SelectPicker} 
                placeholder='Select type' 
                data={Object.values(LinkTypeInfoMap)} 
              />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default LinkDrawer;
